import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CreateSectionBodyDto, Section, SectionObjectType } from '@nusign/picflow-backend-types';
import { Observable, Subject, filter, map, of, switchMap, take, tap } from 'rxjs';
import { defaultAssetSection, sectionVerticalPadding, sectionWidths } from '../lib/default-asset-section';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SocketService } from './socket.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class SectionService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private tenantService = inject(TenantService);
  private socketService = inject(SocketService);
  private onSectionCreated$ = new Subject<Section>();
  private onSectionCreated = this.onSectionCreated$.subscribe(section => {
    this.observableLoader.update<Section[]>(`sections_${section.gallery}`, sections => {
      if (!sections) return;
      if (sections.some(s => s.id === section.id)) return sections;
      return this.fixSections([...sections, section]);
    });
  });

  public onSectionUpdated$ = new Subject<Section>();
  private onSectionUpdated = this.onSectionUpdated$.subscribe(section => {
    this.observableLoader.update<Section[]>(`sections_${section.gallery}`, sections => {
      if (!sections) return;
      const index = sections.findIndex(s => s.id === section.id);
      if (index === -1) return sections;
      const oldSection = sections[index];
      if (oldSection.updated_at > section.updated_at) return sections;
      sections[index] = section;
      return this.fixSections(sections);
    });
  });

  public onSectionDeleted$ = new Subject<Section>();
  private onSectionDeleted = this.onSectionDeleted$.subscribe(section => {
    this.observableLoader.update<Section[]>(`sections_${section.gallery}`, sections => {
      if (!sections) return;
      return sections.filter(s => s.id !== section.id);
    });
  });

  public sectionHeightCache: { [sectionId: string]: number } = {};

  constructor() {
    this.socketService.onSocketEvent.subscribe(event => {
      if (!event) return;

      const section = event.payload?.section as Section;

      if (event.name === 'section.deleted') {
        this.onSectionDeleted$.next(section);
      }

      if (event.name === 'section.created') {
        this.onSectionCreated$.next(section);
      }

      if (event.name === 'section.updated') {
        this.onSectionUpdated$.next(section);
      }
    });
  }

  public getSections(galleryId: string, options: { ignore401Errors?: boolean } = {}) {
    const extra = `?gallery=${galleryId}${this.getPassword(galleryId)}`;
    return this.observableLoader.get(`sections_${galleryId}`, () => {
      return this.http
        .get<{
          data: Section[];
        }>(`${this.config.apiEndpoint}/sections${extra}`, {
          context: new HttpContext().set(IGNORE_AUTH_ERRORS, options.ignore401Errors),
        })
        .pipe(
          filter(Boolean),
          map(resp => this.fixSections(resp.data))
        );
    });
  }

  public createSection(galleryId: string, type: SectionObjectType, position: number): Observable<Section> {
    let params = <CreateSectionBodyDto>{ gallery: galleryId, object: type, position };
    if (type === 'asset') params = { ...params, ...defaultAssetSection };

    return this.http
      .post<Section>(`${this.config.apiEndpoint}/sections`, params)
      .pipe(tap(section => this.onSectionCreated$.next(section)));
  }

  public getOrCreateSection(
    sectionType: SectionObjectType,
    galleryId: string,
    parentSectionId?: string
  ): Observable<Section> {
    return this.getSections(galleryId).pipe(
      take(1),
      switchMap(sections => {
        const section = sections.find(s => {
          if (parentSectionId) return s.id === parentSectionId;
          return s.object === sectionType;
        });
        if (section) return of(section);

        const position = sections.length ? sections.slice().pop().position + 1 : 0;
        return this.createSection(galleryId, sectionType, position);
      })
    );
  }

  public updateSection(section: Section, params: Partial<CreateSectionBodyDto>): Observable<Section> {
    return this.http.patch<Section>(`${this.config.apiEndpoint}/sections/${section.id}`, params).pipe(
      tap(updatedSection => {
        updatedSection.updated_at += 1; // making sure that there is no websocket event, older than this change, that will override this change
        this.onSectionUpdated$.next(updatedSection);
      })
    );
  }

  public deleteSection(section: Section): Observable<undefined> {
    return this.http.delete(`${this.config.apiEndpoint}/sections/${section.id}`).pipe(
      tap(() => {
        this.loader.removeFuzzy('assets_');
        this.tenantService.refreshTenant();
        this.onSectionDeleted$.next(section);
      }),
      map(() => undefined)
    );
  }

  private fixSections(sections: Section[]) {
    sections.forEach(section => {
      if (section['spacing'] === null) section['spacing'] = 0;

      if (!section.width) {
        section.width = section.object === 'text' ? sectionWidths.small : sectionWidths.medium;
      }
      if (!section.vertical_padding) {
        section.vertical_padding = sectionVerticalPadding.medium;
      }
    });
    return sections.filter(s => s.position !== 612512).sort((a, b) => (a.position > b.position ? 1 : -1));
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }
}
